import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowRightLeft, CheckCircle2, ClipboardCheck, UserCheck } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { EmptyState } from '../../../components/EmptyState';
import { useAssetStore } from '../../../store/assetStore';
import { useEmployeeAssetsStore } from '../../../store/employeeAssetsStore';
import { EmployeeCard } from '../../../components/assets/EmployeeAssets';

const selectClass =
  'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-50';

/**
 * Asset assignment — every list is read from the assets service:
 *   approved requests → assets.asset_requests (status 'approved')
 *   in-stock devices  → assets.assets (status 'In Stock')
 *   employees         → people.employees via /assets/employees (no People
 *                       permission needed; includes role, manager, holdings)
 * Handing over calls POST /requests/:id/fulfill or POST /assets/:id/assign.
 */
export function AssetAssignmentView() {
  const { assets, requests, fetchAssets, fetchRequests, fulfillRequest, assignAsset } = useAssetStore();
  const { directory: employees, directoryError, fetchDirectory } = useEmployeeAssetsStore();
  const [searchParams] = useSearchParams();
  const [pickForRequest, setPickForRequest] = useState<Record<string, string>>({});
  // ?employee=<id> preselects the employee (e.g. from the People profile).
  const [direct, setDirect] = useState({ assetId: '', employeeId: searchParams.get('employee') || '', notes: '' });
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    fetchAssets();
    fetchRequests();
    fetchDirectory().catch((e) => toast.error(e.message));
  }, [fetchAssets, fetchRequests, fetchDirectory]);

  const selectedEmployee = employees.find((e) => e.id === direct.employeeId) || null;
  const selectedAsset = assets.find((a) => a.id === direct.assetId) || null;
  const employeeById = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);

  const inStock = useMemo(() => assets.filter((a) => a.status === 'In Stock'), [assets]);
  const approved = requests.filter((r) => r.status === 'approved');
  const recentlyFulfilled = requests.filter((r) => r.status === 'fulfilled').slice(0, 5);

  const handover = async (requestId: string, requester: string) => {
    const assetId = pickForRequest[requestId];
    if (!assetId) { toast.error('Choose the device to hand over'); return; }
    setBusy(requestId);
    try {
      await fulfillRequest(requestId, assetId);
      toast.success(`Device assigned to ${requester}`);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const assignDirect = async () => {
    if (!direct.assetId || !direct.employeeId) { toast.error('Choose a device and an employee'); return; }
    setBusy('direct');
    try {
      const asset = await assignAsset(direct.assetId, direct.employeeId, direct.notes || undefined);
      toast.success(`${asset.asset_code} assigned to ${asset.assignedToName}`);
      setDirect({ assetId: '', employeeId: direct.employeeId, notes: '' });
      fetchDirectory().catch(() => undefined); // refresh holding counts
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        className="mb-0"
        icon={UserCheck}
        title="Asset assignment"
        description="Hand in-stock devices to employees — from approved requests or directly."
      />

      <Card>
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="font-display text-card-title text-foreground">Approved requests waiting for a device</h2>
          <Badge variant={approved.length ? 'warning' : 'muted'}>{approved.length}</Badge>
        </div>
        {approved.length === 0 ? (
          <EmptyState
            className="m-4 border-0"
            icon={<ClipboardCheck />}
            title="Nothing to hand over"
            description="Requests approved in Asset approval appear here until a device is assigned."
          />
        ) : (
          <ul className="divide-y divide-border">
            {approved.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <UserCheck className="size-4 text-primary" /> {r.requester_name}
                    {r.requester_employee_id && employeeById.get(r.requester_employee_id) ? (
                      <span className="text-xs font-normal text-muted-foreground">
                        · {[employeeById.get(r.requester_employee_id)!.code, employeeById.get(r.requester_employee_id)!.designation, r.requester_department].filter(Boolean).join(' · ')}
                        {' · '}holds {employeeById.get(r.requester_employee_id)!.assetCount} asset(s)
                      </span>
                    ) : r.requester_department && <span className="text-xs font-normal text-muted-foreground">· {r.requester_department}</span>}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Requested <span className="font-medium text-foreground">{r.asset_type}</span> · priority {r.priority}
                    {!r.requester_employee_id && <span className="text-destructive-strong"> · no employee record — cannot assign</span>}
                  </p>
                </div>
                <div className="flex w-full items-center gap-2 sm:w-auto">
                  <select
                    className={`${selectClass} sm:w-72`}
                    value={pickForRequest[r.id] || ''}
                    onChange={(e) => setPickForRequest({ ...pickForRequest, [r.id]: e.target.value })}
                    disabled={inStock.length === 0 || !r.requester_employee_id}
                  >
                    <option value="">{inStock.length ? 'Choose an in-stock device' : 'No devices in stock'}</option>
                    {inStock.map((a) => (
                      <option key={a.id} value={a.id}>{a.asset_code} — {a.name}{a.model ? ` (${a.model})` : ''}</option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    onClick={() => handover(r.id, r.requester_name)}
                    disabled={!pickForRequest[r.id] || !r.requester_employee_id}
                    isLoading={busy === r.id}
                  >
                    <CheckCircle2 /> Assign
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-4">
        <h2 className="mb-3 flex items-center gap-2 font-display text-card-title text-foreground">
          <ArrowRightLeft className="size-4 text-primary" /> Assign directly
        </h2>
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Employee</span>
              <select className={selectClass} value={direct.employeeId} onChange={(e) => setDirect({ ...direct, employeeId: e.target.value })}>
                <option value="">{directoryError ? 'Could not load employees' : 'Choose employee'}</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}{e.code ? ` (${e.code})` : ''}{e.department ? ` · ${e.department}` : ''}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Device</span>
              <select className={selectClass} value={direct.assetId} onChange={(e) => setDirect({ ...direct, assetId: e.target.value })}>
                <option value="">{inStock.length ? `Choose device (${inStock.length} in stock)` : 'No devices in stock'}</option>
                {inStock.map((a) => <option key={a.id} value={a.id}>{a.asset_code} — {a.name}{a.model ? ` (${a.model})` : ''}</option>)}
              </select>
            </label>
            {selectedAsset && (
              <p className="text-xs text-muted-foreground">
                {[selectedAsset.category, selectedAsset.manufacturer, selectedAsset.serial_number && `S/N ${selectedAsset.serial_number}`].filter(Boolean).join(' · ') || 'No further details'}
              </p>
            )}
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Note</span>
              <input
                className={selectClass}
                placeholder="Optional"
                value={direct.notes}
                maxLength={300}
                onChange={(e) => setDirect({ ...direct, notes: e.target.value })}
              />
            </label>
            <div className="flex justify-end">
              <Button variant="secondary" onClick={assignDirect} isLoading={busy === 'direct'} disabled={!direct.assetId || !direct.employeeId}>
                Assign device
              </Button>
            </div>
          </div>
          {selectedEmployee ? (
            <EmployeeCard employee={selectedEmployee} />
          ) : (
            <div className="flex items-center justify-center rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Choose an employee to see their role, department, manager and what they already hold.
            </div>
          )}
        </div>
      </Card>

      {recentlyFulfilled.length > 0 && (
        <Card className="p-4">
          <h2 className="mb-2 font-display text-card-title text-foreground">Recently handed over</h2>
          <ul className="divide-y divide-border text-sm">
            {recentlyFulfilled.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-2">
                <span className="truncate text-foreground">{r.requester_name} · {r.asset_type}</span>
                <Badge variant="success">{r.assigned_asset_code || 'Assigned'}</Badge>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
